// Card at sign-up (phase 2): the CONFIRM step between choosing a plan and Stripe Checkout.
// Above the button, exactly the server's sentence (routes/billing.js checkout-preview, from Stripe):
//   "Your free trial ends on [date]. We'll charge [£amount/period] then unless you cancel.
//    Cancel any time in Settings."
// No amount is ever worked out here: if the server cannot read Stripe, the step says so and offers
// a retry, and the button stays off.
//
// Phase 4 (Visa Core Rules, Tables 5-20 and 5-21): the subscription agreement, ON ITS OWN, apart
// from any general terms: the merchant and how to reach them, what is bought, the trial, £0.00
// today, the later charges, how to cancel before the date, the refund policy. Every row is the
// server's (lib/billingNotices), so Checkout and the emails say the same. The click-to-accept is
// on Stripe's Checkout, where the card is entered (consent_collection), and this step says so.
// The last four digits cannot be shown here: the card does not exist until Checkout.
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Skeleton } from '../../ui';
import { messageFromError } from '../../utils/apiError';
import { fetchCheckoutPreview, savePlanChoice, startCheckout } from './startTrial';
import './startTrial.css';

export default function TrialConfirm({ planName, planLabel, billingPeriod, onBack, assign }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    setError(''); setPreview(null);
    try {
      setPreview(await fetchCheckoutPreview(planName, billingPeriod));
    } catch (err) {
      setError(messageFromError(err, 'The price could not be read just now. Please try again.'));
    }
  }, [planName, billingPeriod]);

  useEffect(() => { load(); }, [load]);

  async function proceed() {
    setOpening(true); setError('');
    savePlanChoice(planName, billingPeriod);
    try {
      await startCheckout(planName, billingPeriod, assign ? { assign } : undefined);
    } catch (err) {
      setError(messageFromError(err, 'Checkout could not be opened. Please try again.'));
      setOpening(false);
    }
  }

  const rows = Array.isArray(preview?.disclosure) ? preview.disclosure : [];

  return (
    <section className="st-card" aria-labelledby="st-confirm-title" data-testid="trial-confirm">
      <h2 id="st-confirm-title" className="st-title">Confirm {planLabel}, {billingPeriod === 'annual' ? 'annual' : 'monthly'}</h2>
      {preview && preview.quantity > 1 && (
        <p className="st-text">Team Pro starts at {preview.quantity} seats. Seats follow your member count after that.</p>
      )}
      {!preview && !error && <Skeleton lines={2} label="Working out your trial" />}
      {preview && <p className="st-sentence" data-testid="trial-sentence">{preview.sentence}</p>}
      {rows.length > 0 && (
        <section className="st-agreement" aria-labelledby="st-agreement-title" data-testid="trial-agreement">
          <h3 id="st-agreement-title" className="st-agreement__title">Your subscription agreement</h3>
          <dl className="st-agreement__rows">
            {rows.map((r) => (
              <div className="st-agreement__row" key={r.label}>
                <dt>{r.label}</dt>
                <dd>{r.text}</dd>
              </div>
            ))}
          </dl>
          {preview.consentText && (
            <p className="st-agreement__consent" data-testid="trial-consent">
              On the next page you tick to agree: &ldquo;{preview.consentText}&rdquo;
            </p>
          )}
        </section>
      )}
      {error && <p className="st-error" role="alert" data-testid="trial-confirm-error">{error}</p>}
      <div className="st-actions">
        {error && !preview
          ? <Button variant="confirm" block onClick={load}>Try again</Button>
          : (
            <Button variant="confirm" block disabled={!preview || opening} onClick={proceed} data-testid="trial-continue">
              {opening ? 'Opening Stripe…' : 'Continue to card details'}
            </Button>
          )}
        <Button variant="tertiary" block onClick={onBack} disabled={opening}>Back to plans</Button>
      </div>
      <p className="st-fine">Your card details go to Stripe, never to Aidey. Aidey is available to UK customers for now.</p>
    </section>
  );
}
